import { tr } from '@/lib/i18n-core';
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CARE_TEAM_ROLE } from '@his/shared';
import { del, get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, Input, Select } from '@/components/ui/primitives';
import { ReasonDialog } from '@/components/ui/reason-dialog';
import { useToast } from '@/components/ui/toast';
import { ChoiceButtons } from '@/components/ui/choice-buttons';

function careRoleLabel(role: string): string {
  return (CARE_TEAM_ROLE as readonly string[]).includes(role) ? tr('opd', `care_role_${role}`) : role;
}

interface AttachmentRow {
  id: string;
  title: string;
  fileKey: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  voidedAt: string | null;
  voidReason: string | null;
}

export function AttachmentsPanel({ encounterId, canWrite }: { encounterId: string; canWrite: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [withdrawing, setWithdrawing] = useState<{ id: string; title: string } | null>(null);
  const attachments = useQuery({ queryKey: ['attachments', encounterId], queryFn: () => get<AttachmentRow[]>(`/encounters/${encounterId}/attachments`) });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['attachments', encounterId] });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const presigned = await post<{ key: string; url: string; method: string; headers?: Record<string, string> }>('/files/presign-upload', {
        folder: 'patient-documents',
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      });
      const response = await fetch(presigned.url, { method: presigned.method, headers: presigned.headers, body: file });
      if (!response.ok) throw new Error(`Could not upload ${file.name}`);
      return post(`/encounters/${encounterId}/attachments`, { title: title.trim() || file.name, fileKey: presigned.key });
    },
    onSuccess: () => {
      toast.success(tr('opd', 'attached'));
      setTitle('');
      refresh();
    },
    onError: (error) => toast.error(tr('opd', 'could_not_attach_the_file'), describeError(error)),
  });

  const withdraw = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => post(`/attachments/${id}/void`, { reason }),
    onSuccess: () => {
      setWithdrawing(null);
      refresh();
    },
    onError: (error) => toast.error(tr('opd', 'could_not_withdraw_the_file'), describeError(error)),
  });

  const open = async (key: string) => {
    const { url } = await post<{ url: string }>('/files/presign-download', { key });
    window.open(url, '_blank', 'noopener');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('opd', 'attachments')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {canWrite && (
          <div className="flex flex-wrap items-end gap-2">
            <Field label={tr('opd', 'title')}>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={tr('opd', 'ecg_outside_report')} maxLength={160} />
            </Field>
            <input
              ref={input}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file);
                e.target.value = '';
              }}
            />
            <Button variant="outline" onClick={() => input.current?.click()} loading={upload.isPending}>
              {tr('opd', 'add_file_or_photo')}
            </Button>
          </div>
        )}
        {(attachments.data ?? []).length === 0 && <p className="text-sm text-muted-foreground">{tr('opd', 'no_files_attached')}</p>}
        {attachments.data?.map((row) => (
          <div key={row.id} className="flex items-center justify-between rounded-md border p-2 text-sm">
            <div>
              <p className={row.voidedAt ? 'line-through' : ''}>{row.title}</p>
              <p className="text-xs text-muted-foreground">
                {dateTime(row.createdAt)} · {Math.round(row.sizeBytes / 1024)} {tr('opd', 'kb')}{row.voidReason ? ` · ${tr('opd', 'withdrawn_reason', { reason: row.voidReason })}` : ''}
              </p>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => void open(row.fileKey)}>
                {tr('opd', 'open_2')}
              </Button>
              {canWrite && !row.voidedAt && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setWithdrawing({ id: row.id, title: row.title })}
                >
                  {tr('opd', 'withdraw')}
                </Button>
              )}
            </div>
          </div>
        ))}
      </CardContent>
      {withdrawing && (
        <ReasonDialog
          title={tr('opd', 'withdraw_file_title', { title: withdrawing.title })}
          description={tr('opd', 'withdraw_file_description')}
          minLength={5}
          maxLength={400}
          submitLabel={tr('opd', 'withdraw')}
          destructive
          loading={withdraw.isPending}
          onSubmit={(reason) => withdraw.mutate({ id: withdrawing.id, reason })}
          onClose={() => setWithdrawing(null)}
        />
      )}
    </Card>
  );
}

interface CareTeamRow {
  id: string;
  role: string;
  user: { id: string; name: string; designation: string | null } | null;
}

export function CareTeamPanel({ appointmentId, canWrite }: { appointmentId: string; canWrite: boolean }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<(typeof CARE_TEAM_ROLE)[number]>('assisting');
  const team = useQuery({ queryKey: ['care-team', appointmentId], queryFn: () => get<CareTeamRow[]>(`/appointments/${appointmentId}/care-team`) });
  const staff = useQuery({
    queryKey: ['staff-for-care-team'],
    queryFn: () => get<{ id: string; name: string; designation: string | null }[]>('/care-team/candidates'),
    enabled: canWrite && can('encounter.write', 'appointment.manage'),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['care-team', appointmentId] });
  const add = useMutation({
    mutationFn: () => post(`/appointments/${appointmentId}/care-team`, { userId, role }),
    onSuccess: () => {
      setUserId('');
      refresh();
    },
    onError: (error) => toast.error(tr('opd', 'could_not_add_the_colleague'), describeError(error)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => del(`/appointments/${appointmentId}/care-team/${id}`),
    onSuccess: refresh,
    onError: (error) => toast.error(tr('opd', 'could_not_remove_the_colleague'), describeError(error)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('opd', 'care_team')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {(team.data ?? []).length === 0 && <p className="text-sm text-muted-foreground">{tr('opd', 'only_the_treating_doctor')}</p>}
        {team.data?.map((member) => (
          <div key={member.id} className="flex items-center justify-between text-sm">
            <span>
              {member.user?.name ?? tr('opd', 'unknown')} <Badge variant="secondary">{careRoleLabel(member.role)}</Badge>
            </span>
            {canWrite && (
              <Button size="sm" variant="ghost" onClick={() => remove.mutate(member.id)}>
                {tr('opd', 'remove')}
              </Button>
            )}
          </div>
        ))}
        {canWrite && staff.data && (
          <div className="flex flex-wrap items-end gap-2">
            <Select value={userId} onChange={(e) => setUserId(e.target.value)} className="w-56" aria-label={tr('opd', 'colleague')}>
              <option value="">{tr('opd', 'add_a_colleague')}</option>
              {staff.data.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </Select>
            <ChoiceButtons
              inline
              label={tr('opd', 'role')}
              value={role}
              size="sm"
              options={CARE_TEAM_ROLE.map((value) => ({ value, label: careRoleLabel(value) }))}
              onChange={(next) => setRole(next)}
            />
            <Button size="sm" onClick={() => add.mutate()} disabled={!userId} loading={add.isPending}>
              {tr('opd', 'add')}
            </Button>
          </div>
        )}
        {add.error && <Alert variant="destructive">{describeError(add.error)}</Alert>}
      </CardContent>
    </Card>
  );
}
